import { supabase } from '@/integrations/supabase/client';
import { Step, PromptModule, PromptField } from '@/types/roadmap';
import { syncFieldSidebarTools } from '@/utils/sidebarTools';
import { buildPromptFieldMetadata } from '@/utils/promptFieldMetadata';

export async function updateSteps(templateId: string, steps: Step[]): Promise<void> {
  if (!steps) { // Allow empty array to signify all steps are deleted
    steps = [];
  }
  if (!Array.isArray(steps)) {
    console.error('updateSteps: steps parameter is not an array.');
    return;
  }

  // Update or Insert steps from the client
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    await updateSingleStep(templateId, step, i + 1);
  }
}

async function updateSingleStep(templateId: string, step: Step, position: number): Promise<void> {
  console.log(`updateSingleStep: Updating/inserting step ${step.id}`);

  const stepData = {
    title: step.title,
    description: step.description,
    position: position,
    roadmap_id: templateId,
    iframe_src: step.walkthrough?.iframeSrc || null,
    download_link: step.walkthrough?.downloadLink || null,
    download_text: step.walkthrough?.downloadText || null,
    resources: step.resources,
    is_open_default: step.isOpenDefault || false,
    updated_at: new Date().toISOString()
  };

  // Check if step exists to decide on insert or update
  // This part might need refinement if new steps don't have a pre-generated UUID matching `step.id`
  // For now, assuming `step.id` is always valid for an existing or new record that will be created with this ID
  // by the form logic before calling updateSteps.

  const { data, error: selectError } = await supabase
    .from('steps')
    .select('id')
    .eq('id', step.id)
    .maybeSingle();

  let stepError;
  if (selectError && selectError.code !== 'PGRST116') { // PGRST116: 0 rows
    console.error('updateSingleStep: Error checking if step exists:', selectError);
    throw selectError;
  }

  if (data) { // Step exists, update it
    const { error } = await supabase
      .from('steps')
      .update(stepData)
      .eq('id', step.id);
    stepError = error;
  } else { // Step doesn't exist, insert it
    const { error } = await supabase
      .from('steps')
      .insert({ id: step.id, ...stepData, created_at: new Date().toISOString() }); // Ensure `created_at` on new inserts
    stepError = error;
  }


  if (stepError) {
    console.error('updateSingleStep: Error updating/inserting step:', stepError);
    throw stepError;
  }

  console.log(`updateSingleStep: Successfully updated/inserted step ${step.id}`);

  // Update prompt module and related data
  if (step.promptModule) {
    await updatePromptModule(step, templateId);
  }

  // Update links
  if (step.links && Array.isArray(step.links)) {
    await updateStepLinks(step);
  }
}

async function updatePromptModule(step: Step, templateId: string): Promise<void> {
  if (!step.promptModule || !step.id) return;

  // Check if prompt module needs soft deletion (if step itself is marked for deletion)
  // This logic might need adjustment if prompt_modules have their own is_deleted flag
  // For now, assume prompt_modules are tied to the step's lifecycle or handled by cascades.
  // If a step is soft-deleted, its prompt_module effectively becomes inactive.

  const { error: moduleError } = await supabase
    .from('prompt_modules')
    .update({
      title: step.promptModule.title,
      introduction: step.promptModule.introduction,
      final_output_format: step.promptModule.finalOutputFormat,
      copy_button_text: step.promptModule.copyButtonText,
      step_id: step.id, // This ensures it's linked to the correct step
      updated_at: new Date().toISOString()
      // If prompt_modules table gets an is_deleted flag, set it here based on step.is_deleted
    })
    .eq('id', step.promptModule.id);

  if (moduleError) {
    console.error('updatePromptModule: Error updating prompt module:', moduleError);
    throw moduleError;
  }

  // Update prompt fields
  if (step.promptModule.fields && Array.isArray(step.promptModule.fields)) {
    await updatePromptFields(step.promptModule, templateId, step.id);
  }

  // Update prompt instructions
  if (step.promptModule.instructions && Array.isArray(step.promptModule.instructions)) {
    await updatePromptInstructions(step.promptModule);
  }
}

async function updatePromptFields(promptModule: PromptModule, templateId: string, stepId: string): Promise<void> {
  const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

  // Get current prompt field IDs from the database for this module
  const { data: dbFields, error: dbFieldsError } = await supabase
    .from('prompt_fields')
    .select('id, metadata')
    .eq('prompt_module_id', promptModule.id);

  if (dbFieldsError) {
    console.error('updatePromptFields: Error fetching existing prompt fields:', dbFieldsError);
    throw dbFieldsError;
  }

  const dbFieldIds = dbFields ? dbFields.map(f => f.id) : [];
  const existingMetadataByFieldId = new Map(
    (dbFields || []).map((field) => [field.id, field.metadata])
  );
  const clientFieldIds = promptModule.fields ? promptModule.fields.map(f => f.id) : [];

  // Conservative approach: Only delete fields if template is fully loaded AND explicitly marked for deletion
  // This prevents accidental deletion when template loads incompletely
  const fieldsToDelete = dbFieldIds.filter(id => {
    // Only delete if:
    // 1. Field is not in client state (suggesting it was intentionally removed)
    // 2. We have a reasonable number of client fields (suggesting full template load)
    // 3. The missing field isn't just due to incomplete loading
    return !clientFieldIds.includes(id) &&
      clientFieldIds.length > 0 &&
      promptModule.fields &&
      promptModule.fields.length > 0;
  });

  if (fieldsToDelete.length > 0) {
    // Additional safety check: only delete if the ratio suggests intentional removal, not incomplete loading
    const deletionRatio = fieldsToDelete.length / (dbFieldIds.length || 1);

    if (deletionRatio < 0.8) { // Only delete if less than 80% of fields would be removed
      console.log('⚠️ CONTROLLED_DELETE: About to delete fields:', {
        fieldsToDelete: fieldsToDelete,
        deletionRatio: deletionRatio,
        reason: 'Fields appear to be intentionally removed from template'
      });

      const { error: deleteError } = await supabase
        .from('prompt_fields')
        .delete()
        .in('id', fieldsToDelete);

      if (deleteError) {
        console.error('updatePromptFields: Error deleting prompt fields:', deleteError);
        throw deleteError;
      }
      console.log('updatePromptFields: Deleted prompt fields:', fieldsToDelete);
    } else {
      console.log('🛡️ DELETION_PREVENTED: Skipping deletion due to high ratio:', {
        fieldsToDelete: fieldsToDelete,
        deletionRatio: deletionRatio,
        reason: 'High deletion ratio suggests incomplete template load, not intentional removal'
      });
    }
  } else {
    console.log('✅ NO_DELETION_NEEDED: No fields to delete');
  }

  if (!promptModule.fields || promptModule.fields.length === 0) {
    // All fields were removed, or there were none to begin with.
    // Deletion logic above handles this.
    return;
  }

  // Update or Insert fields from the client
  for (const field of promptModule.fields) {
    const isUUID = uuidRegex.test(field.id);
    const fieldExistsInDb = isUUID && dbFieldIds.includes(field.id);

    // Debug logging for interactive_prompt fields
    if (field.tag === 'interactive_prompt') {
      const mergedMetadata = buildPromptFieldMetadata(
        field,
        fieldExistsInDb ? existingMetadataByFieldId.get(field.id) : undefined
      );
      console.log('💾 ADMIN_SAVE: Saving interactive_prompt field:', {
        id: field.id,
        label: field.label,
        tag: field.tag,
        isExistingFieldByUUID: fieldExistsInDb,
        slot: mergedMetadata.slot,
        tabId: mergedMetadata.tabId,
        placeholder: field.placeholder?.substring(0, 100) + '...'
      });
    }

    const fieldData = {
      label: field.label,
      placeholder: field.placeholder,
      example: field.example, // Added: HTML content for examples
      tag: field.tag,
      value: field.value,
      position: field.position || 0,
      prompt_module_id: promptModule.id,
      video_player_options: field.video_player_options || null,
      lesson_library_id: field.lesson_library_id || null,
      attachments: Array.isArray(field.attachments) && field.attachments.length > 0 ? field.attachments : null,
      ...(field.client_id && uuidRegex.test(field.client_id) ? { client_id: field.client_id } : {}),
      // Store AI Companion config in metadata JSONB
      metadata: buildPromptFieldMetadata(
        field,
        fieldExistsInDb ? existingMetadataByFieldId.get(field.id) : undefined
      ),
      updated_at: new Date().toISOString()
    };

    if (fieldExistsInDb) {
      // Field exists in database, update by primary key
      const { error: fieldError, data: updateResult } = await supabase
        .from('prompt_fields')
        .update(fieldData)
        .eq('id', field.id)
        .select();

      if (fieldError) {
        console.error(`updatePromptFields: Error updating prompt field with id ${field.id}:`, fieldError);
        throw fieldError;
      }

      await syncFieldSidebarTools({
        roadmapId: templateId,
        stepId,
        fieldId: field.id,
        links: field.tag === 'video_player'
          ? field.video_player_options?.links || []
          : []
      });


    } else {
      // Field does not exist yet. Insert (and preserve provided UUID if valid)
      const timestamp = new Date().toISOString();
      const newFieldPayload = {
        ...(isUUID ? { id: field.id } : {}),
        label: field.label,
        placeholder: field.placeholder,
        example: field.example, // Added: HTML content for examples
        tag: field.tag,
        value: field.value,
        position: field.position || 0,
        prompt_module_id: promptModule.id,
        video_player_options: field.video_player_options || null,
        lesson_library_id: field.lesson_library_id || null,
        attachments: Array.isArray(field.attachments) && field.attachments.length > 0 ? field.attachments : null,
        ...(field.client_id ? { field_id: field.client_id } : {}),
        ...(field.client_id && uuidRegex.test(field.client_id) ? { client_id: field.client_id } : {}),
        // Store AI Companion config in metadata JSONB
        metadata: buildPromptFieldMetadata(field),
        created_at: timestamp,
        updated_at: timestamp
      };

      const { data: insertedRecord, error: fieldError } = await supabase
        .from('prompt_fields')
        .insert(newFieldPayload)
        .select('id')
        .single();

      if (fieldError) {
        console.error(`updatePromptFields: Error inserting prompt field (id ${field.id}):`, fieldError);
        throw fieldError;
      }

      if (insertedRecord) {
        console.log(`updatePromptFields: Inserted prompt field with DB UUID ${insertedRecord.id} for module ${promptModule.id}.`);
        await syncFieldSidebarTools({
          roadmapId: templateId,
          stepId,
          fieldId: insertedRecord.id,
          links: field.tag === 'video_player'
            ? field.video_player_options?.links || []
            : []
        });
      }
    }
  }
}

async function updatePromptInstructions(promptModule: PromptModule): Promise<void> {
  for (let j = 0; j < promptModule.instructions.length; j++) {
    const instruction = promptModule.instructions[j];
    const { error: instructionError } = await supabase
      .from('prompt_instructions')
      .update({
        instruction: instruction,
        position: j + 1,
        prompt_module_id: promptModule.id,
        updated_at: new Date().toISOString()
      })
      .eq('prompt_module_id', promptModule.id)
      .eq('position', j + 1);

    if (instructionError) {
      console.error('updatePromptInstructions: Error updating prompt instruction:', instructionError);
      throw instructionError;
    }
  }
}

async function updateStepLinks(step: Step): Promise<void> {
  if (!step.links || !step.id) return;

  for (const link of step.links) {
    const { error: linkError } = await supabase
      .from('links')
      .update({
        label: link.label,
        url: link.url,
        step_id: step.id,
        updated_at: new Date().toISOString()
      })
      .eq('step_id', step.id)
      .eq('label', link.label);

    if (linkError) {
      console.error('updateStepLinks: Error updating link:', linkError);
      throw linkError;
    }
  }
}
